import React from 'react'
import { styled, IconButton, Box } from '@mui/material'
import {
  ImageInput as ReactAdminImageInput,
  useNotify,
  useRecordContext,
  maxLength,
  required,

} from 'react-admin'
import CropIcon from '@mui/icons-material/Crop'
import get from 'lodash/get'
import { FilePreviewProps, ImageInputProps, ImagePreviewProps } from './type'
import { ImageField, MultipleImageField } from '../fields'
import { isBlob } from '../../utils/validators'
import { useFormContext, useWatch, useFormState } from 'react-hook-form'
import CropImage from '../cropper/CropImage'
import { COMMON_FIELDS } from '../../providers/localization/constant'
import { isArray, isNil } from 'lodash'
const ImageInputStyled = styled(ReactAdminImageInput)(({ theme }) => ({
  position: 'relative',
  ['& .RaFileInput-dropZone']: {},
  ['& .MuiFormHelperText-root']: {},
  ['& .RaFileInput-dropZone svg']: {},
  ['& .previews']: {
    ['& .RaFileInput-removeButton']: {
      color: theme.palette.error,
      ['& img']: {
        transition: theme.transitions.create(['opacity'], {
          duration: theme.transitions.duration.standard,
        }),
      },
      ['&:hover']: {
        ['& img']: {
          opacity: 0.4,
        },
      },
    },
  },
  ['& .RaFileInputPreview-removeButton']: {
    zIndex: 1,
  },
  ['& .RaFileInput-preview']: {},
}))

const ImagePreviewField = styled(ImageField)(() => ({
  '& .RaImageField-image': {
    margin: 0,
    width: '100%',
    height: 200,
    objectFit: 'cover',
  },
}))

const CropButtonWrapper = styled(Box)(({ theme }) => ({
  position: 'absolute',
  right: 35,
  top: 0,
  backgroundColor: theme.palette.background.default,
  borderRadius: '50%',
}))

const defaultFileInputOptions = {
  maxFiles: 5,
}

export const ImagePreview: React.FC<ImagePreviewProps> = ({ fallbackSource = null, ...props }) => {
  const record = useRecordContext()
  const fallbackUrl = fallbackSource ? get(record, fallbackSource) : null
  const { src: uploadSrc } = record
  const path = uploadSrc ? uploadSrc : fallbackUrl

  return <ImagePreviewField preSrc={!uploadSrc || isBlob(path) ? path : null} {...props} />
}

export const MultipleImagePreview: React.FC<FilePreviewProps> = (props) => {
  const { source, fallbackSource, title } = props
  const record = useRecordContext()
  const { src: uploadSrc } = record
  const path = uploadSrc ? source : fallbackSource
  const titleFallback = uploadSrc ? 'title' : title
  return <MultipleImageField source={path} title={titleFallback} target='_blank' />
}

export const IconImageInput: React.FC<ImageInputProps> = ({
  width = '100%',
  height = 'auto',
  fallbackSource = null,
  initialAspectRatio = 16 / 9,
  aspectRatio = 16 / 9,
  initCrop = false,
  deleteFileField = null,
  fileInputOptions = defaultFileInputOptions,
  ...props
}) => {
  const notify = useNotify()
  const { getValues, setValue } = useFormContext()
  const { maxFiles } = fileInputOptions
  const [openDialogCrop, setOpenDialogCrop] = React.useState(false)
  const handleCloseDialogCrop = (): void => {
    setOpenDialogCrop(false)
  }

  const files = useWatch({ name: props.source })
  const disabled = files?.length >= maxFiles
  const { errors } = useFormState()
  const disableStyle = disabled
    ? {
        '& .RaFileInput-dropZone': {
          cursor: 'default',
          background: 'transparent',
          border: '1px solid',
          borderColor: errors.icon ? 'error.main' : 'common.black',
          width,
          height,
          ['& .previews']: {
            width,
            height,
          },
        },
      }
    : {}

  const baseStyle = {
    '& .MuiFormHelperText-root, .RaLabeled-label, .RaFileInput-dropZone > *': {
      color: errors.icon ? 'error.main' : 'unset',
    },
    ...disableStyle,
  }

  return (
    <>
      <ImageInputStyled
        onChange={(): void => {
          // trigger(props.source)
          setOpenDialogCrop(true)
        }}
        maxSize={10000000}
        helperText={`${COMMON_FIELDS}.file.helper`}
        options={{
          onDropRejected: (fileRejections): void => {
            fileRejections.forEach(({ file, errors }) => {
              notify(`${file.name} - ${errors[0].message}`, {
                type: 'warning',
                autoHideDuration: 5000,
              })
            })
          },
          maxFiles,
          disabled,
        }}
        {...props}
        sx={{ ...baseStyle, ...disableStyle }}
        labelMultiple={
          disabled ? 'ra.input.file.upload_max_file_reach' : 'ra.input.file.upload_several'
        }
        validate={[maxLength(maxFiles, 'ra.notification.file_count_exceed_error'), required('ra.validation.thumbnail_required')]}
        onRemove={({ name, rawFile }): void => {
          if (!deleteFileField || !name || rawFile) return
          const prevFields = getValues()
          const targetField = prevFields[deleteFileField]
          const deleteFieldValues = isNil(targetField) || !isArray(targetField) ? [] : targetField
          setValue(deleteFileField, [...deleteFieldValues, name])
        }}
      >
        {props.multiple ? (
          <>
            <MultipleImagePreview source='src' title='name' fallbackSource={fallbackSource} />
            {initCrop ? (
              <CropButtonWrapper>
                <IconButton size='small' sx={{ p: 0.875 }}>
                  <CropIcon fontSize='small' />
                </IconButton>
              </CropButtonWrapper>
            ) : null}
          </>
        ) : (
          <ImagePreview source='src' fallbackSource={fallbackSource} />
        )}
      </ImageInputStyled>
      {initCrop ? (
        <CropImage
          source={props.source}
          initialAspectRatio={initialAspectRatio}
          aspectRatio={aspectRatio}
          open={openDialogCrop}
          handleClose={handleCloseDialogCrop}
        />
      ) : null}
    </>
  )
}
